/**
 * Lifecycle transition notifications — Sprint 9 WI-3 Step 2.
 *
 * Builds immediate toast + notification-center payloads when a challenge reaches
 * a terminal state, always pointing the user toward My Arena → Completed.
 *
 * Pure presentation + routing metadata. Does not mutate lifecycle state.
 * Principle: "One lifecycle state → many presentations."
 */
import { NotificationType, NotificationPriority } from '../notifications/notificationTypes';
import { COMPLETED_VIEW_IN_ARENA, SENT_VIEW_IN_ARENA } from '../constants/productCopy';

/** bootstrapAction value consumed by Lockbox → My Arena → Completed tab. */
export const MY_ARENA_COMPLETED_NAV = 'my-arena-completed';
/** bootstrapAction value consumed by Lockbox → My Arena → Sent tab (Sprint 10 WI-6). */
export const MY_ARENA_SENT_NAV = 'my-arena-sent';

export const LifecycleTransition = Object.freeze({
  DECLINED_BY_RECIPIENT: 'declined_by_recipient',
  DECLINED_BY_SENDER: 'declined_by_sender',
  SETTLED_LOST: 'settled_lost',
  SETTLED_DRAW: 'settled_draw',
  OPEN_LOCK_MATCHED: 'open_lock_matched',
  OPEN_LOCK_EXPIRED: 'open_lock_expired',
  OPEN_LOCK_CANCELED: 'open_lock_canceled',
  TICKET_ACCEPTED: 'ticket_accepted',
  TICKET_EXPIRED: 'ticket_expired',
  TICKET_CANCELED: 'ticket_canceled',
  TICKET_DECLINED: 'ticket_declined',
});

/**
 * Transitions whose notification deep-links into the Sent surface instead of
 * Completed.
 *
 * Ratified routing rule (Sprint 10 WI-6): a notification must route to the tab
 * where the referenced object is visible *after* the transition. Sent shows only
 * sender-managed **non-terminal** objects; terminal outcomes live in Completed.
 *
 * Every modeled ticket/open-lock transition today is terminal (accepted /
 * expired / canceled / matched), so the object has already left Sent by the time
 * the notification fires — routing them to Sent would land the user on a tab
 * where the item no longer appears. They therefore stay on Completed.
 *
 * This set is intentionally empty: the `MY_ARENA_SENT_NAV` infrastructure is
 * retained for future **non-terminal** sender events (e.g. a counter-offer on an
 * outgoing pending challenge) that remain visible in Sent after firing.
 *
 * Routing only ever changes `meta.navigate` + the CTA copy; every `dedupeKey` is
 * preserved so the server-side dedupe ledger is unaffected.
 */
const SENT_ROUTED_TRANSITIONS = new Set([]);

/** @returns {boolean} whether a transition deep-links into the Sent surface. */
export function isSentRoutedTransition(transition) {
  return SENT_ROUTED_TRANSITIONS.has(transition);
}

function opponentName(bet, viewerId) {
  const otherId = bet?.creatorId === viewerId ? bet?.recipientId : bet?.creatorId;
  return bet?.participants?.find((p) => p.id === otherId)?.name || 'Your opponent';
}

function challengeLabel(bet) {
  return bet?.matchup?.description || 'Challenge';
}

function openLockLabel(lock) {
  return lock?.wager || (lock?.sport ? `${lock.sport} open lock` : 'Open Arena lock');
}

function ticketPasserName(ticket) {
  return ticket?.intendedRecipientDisplayName
    || ticket?.declinedByDisplayName
    || 'Someone';
}

function ticketLabel(ticket) {
  const a = ticket?.teamA || 'Team A';
  const b = ticket?.teamB || 'Team B';
  return `${a} vs ${b}`;
}

function itemEntityId(item) {
  return item?.id ?? item?.ticketCode ?? null;
}

/**
 * @param {string} transition a `LifecycleTransition` value
 * @param {object} item challenge, ticket, or open-lock record
 * @param {{ viewerId?: string|null }} [opts]
 * @returns {{ toastTitle: string, toastMessage: string, toastVariant: string, notificationTitle: string, notificationBody: string, dedupeKey: string, notificationType: string, priority: string, meta: object, viewCta: string }|null}
 */
export function buildLifecycleTransitionPayload(transition, item, { viewerId = null } = {}) {
  const entityId = itemEntityId(item);
  if (!entityId || !transition) return null;
  const routesToSent = SENT_ROUTED_TRANSITIONS.has(transition);
  const cta = routesToSent ? SENT_VIEW_IN_ARENA : COMPLETED_VIEW_IN_ARENA;
  const metaBase = { navigate: routesToSent ? MY_ARENA_SENT_NAV : MY_ARENA_COMPLETED_NAV };

  const payload = buildTransitionBody(transition, item, viewerId, cta, metaBase);
  return payload ? { ...payload, viewCta: cta } : null;
}

/**
 * Builds the per-transition presentation body. `cta` + `metaBase` are injected so
 * routing (Completed vs Sent) is decided once by the caller.
 */
function buildTransitionBody(transition, item, viewerId, cta, metaBase) {
  switch (transition) {
    case LifecycleTransition.DECLINED_BY_RECIPIENT: {
      const name = opponentName(item, viewerId);
      const label = challengeLabel(item);
      return {
        toastTitle: 'Challenge declined',
        toastMessage: `${name} declined your challenge. Your entry was returned. ${cta}`,
        toastVariant: 'success',
        notificationTitle: 'Challenge declined',
        notificationBody: `${name} declined ${label}. Your entry was returned.`,
        dedupeKey: `challenge_declined_recipient_${item.id}`,
        notificationType: NotificationType.CHALLENGE,
        priority: NotificationPriority.MEDIUM,
        meta: { ...metaBase, challengeId: item.id },
      };
    }
    case LifecycleTransition.DECLINED_BY_SENDER: {
      const name = opponentName(item, viewerId);
      const label = challengeLabel(item);
      return {
        toastTitle: 'Challenge canceled',
        toastMessage: `${name} canceled the challenge before it was accepted. ${cta}`,
        toastVariant: 'success',
        notificationTitle: 'Challenge canceled',
        notificationBody: `${name} canceled ${label} before you accepted.`,
        dedupeKey: `challenge_declined_sender_${item.id}`,
        notificationType: NotificationType.CHALLENGE,
        priority: NotificationPriority.MEDIUM,
        meta: { ...metaBase, challengeId: item.id },
      };
    }
    case LifecycleTransition.SETTLED_LOST: {
      const name = opponentName(item, viewerId);
      const label = challengeLabel(item);
      const winnerName = item?.settlement?.winningPlayerName
        || item?.finalOutcome?.winningPlayerName
        || name;
      return {
        toastTitle: 'Challenge settled',
        toastMessage: `${label} — ${winnerName} won. ${cta}`,
        toastVariant: 'success',
        notificationTitle: 'Challenge settled',
        notificationBody: `${label} settled. ${winnerName} won.`,
        dedupeKey: `challenge_settled_lost_${item.id}`,
        notificationType: NotificationType.CHALLENGE,
        priority: NotificationPriority.MEDIUM,
        meta: { ...metaBase, challengeId: item.id },
      };
    }
    case LifecycleTransition.SETTLED_DRAW: {
      const label = challengeLabel(item);
      return {
        toastTitle: 'Challenge settled',
        toastMessage: `${label} ended in a tie. ${cta}`,
        toastVariant: 'success',
        notificationTitle: 'Challenge settled',
        notificationBody: `${label} ended in a tie.`,
        dedupeKey: `challenge_settled_draw_${item.id}`,
        notificationType: NotificationType.CHALLENGE,
        priority: NotificationPriority.MEDIUM,
        meta: { ...metaBase, challengeId: item.id },
      };
    }
    case LifecycleTransition.OPEN_LOCK_MATCHED: {
      const label = openLockLabel(item);
      const acceptor = item.acceptedByName || item.acceptorUsername || 'An opponent';
      return {
        toastTitle: 'Open lock matched',
        toastMessage: `${acceptor} joined your open lock (${label}). ${cta}`,
        toastVariant: 'success',
        notificationTitle: 'Open lock matched',
        notificationBody: `${acceptor} joined ${label}.`,
        dedupeKey: `open_lock_matched_${item.id}`,
        notificationType: NotificationType.CHALLENGE,
        priority: NotificationPriority.MEDIUM,
        meta: { ...metaBase, openLockId: item.id },
      };
    }
    case LifecycleTransition.OPEN_LOCK_EXPIRED: {
      const label = openLockLabel(item);
      return {
        toastTitle: 'Open lock expired',
        toastMessage: `Your open lock expired before anyone joined (${label}). ${cta}`,
        toastVariant: 'success',
        notificationTitle: 'Open lock expired',
        notificationBody: `${label} expired before anyone joined.`,
        dedupeKey: `open_lock_expired_${item.id}`,
        notificationType: NotificationType.CHALLENGE,
        priority: NotificationPriority.MEDIUM,
        meta: { ...metaBase, openLockId: item.id },
      };
    }
    case LifecycleTransition.OPEN_LOCK_CANCELED: {
      const label = openLockLabel(item);
      return {
        toastTitle: 'Open lock removed',
        toastMessage: `You removed your open lock (${label}). ${cta}`,
        toastVariant: 'success',
        notificationTitle: 'Open lock removed',
        notificationBody: `You canceled ${label} in Open Arena.`,
        dedupeKey: `open_lock_canceled_${item.id}`,
        notificationType: NotificationType.CHALLENGE,
        priority: NotificationPriority.LOW,
        meta: { ...metaBase, openLockId: item.id },
      };
    }
    case LifecycleTransition.TICKET_ACCEPTED: {
      const label = ticketLabel(item);
      return {
        toastTitle: 'Ticket accepted',
        toastMessage: `Someone accepted your challenge ticket (${label}). ${cta}`,
        toastVariant: 'success',
        notificationTitle: 'Ticket accepted',
        notificationBody: `Your ticket for ${label} was accepted.`,
        dedupeKey: `ticket_accepted_${item.ticketCode}`,
        notificationType: NotificationType.CHALLENGE,
        priority: NotificationPriority.MEDIUM,
        meta: { ...metaBase, ticketCode: item.ticketCode },
      };
    }
    case LifecycleTransition.TICKET_EXPIRED: {
      const label = ticketLabel(item);
      return {
        toastTitle: 'Ticket expired',
        toastMessage: `Your challenge ticket expired (${label}). ${cta}`,
        toastVariant: 'success',
        notificationTitle: 'Ticket expired',
        notificationBody: `${label} expired before it was accepted.`,
        dedupeKey: `ticket_expired_${item.ticketCode}`,
        notificationType: NotificationType.CHALLENGE,
        priority: NotificationPriority.MEDIUM,
        meta: { ...metaBase, ticketCode: item.ticketCode },
      };
    }
    case LifecycleTransition.TICKET_CANCELED: {
      const label = ticketLabel(item);
      return {
        toastTitle: 'Ticket canceled',
        toastMessage: `You canceled your challenge ticket (${label}). ${cta}`,
        toastVariant: 'success',
        notificationTitle: 'Ticket canceled',
        notificationBody: `You canceled the ticket for ${label}.`,
        dedupeKey: `ticket_canceled_${item.ticketCode}`,
        notificationType: NotificationType.CHALLENGE,
        priority: NotificationPriority.LOW,
        meta: { ...metaBase, ticketCode: item.ticketCode },
      };
    }
    case LifecycleTransition.TICKET_DECLINED: {
      const label = ticketLabel(item);
      const passer = ticketPasserName(item);
      return {
        toastTitle: 'Ticket passed on',
        toastMessage: `${passer} passed on your challenge ticket (${label}). ${cta}`,
        toastVariant: 'success',
        notificationTitle: 'Ticket passed on',
        notificationBody: `${passer} passed on the ticket for ${label}.`,
        dedupeKey: `ticket_declined_${item.ticketCode}`,
        notificationType: NotificationType.CHALLENGE,
        priority: NotificationPriority.MEDIUM,
        meta: { ...metaBase, ticketCode: item.ticketCode },
      };
    }
    default:
      return null;
  }
}
